import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Select as MuiSelect } from "@mui/material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { DateTimePickerComponent } from "../../../Playlists/components/DateTimePicker";
import { UserModalContainer } from "styles/flex";
import { ModalStyle } from "../AddTreatmentModal/styles";
import { ADD_TREATMENT, GET_PROGRAMS } from "./queries";
import { AddTreatmentFormInputs, AddTreatmentProps } from "./types";
import { useSpecialistDropdown } from "hooks/useSpecialistDropDown";
import { GET_TREATMENT_HISTORY } from "../TreatmentHistoryTable/queries";
import { TreatmentHistoryResponse } from "../TreatmentHistoryTable/types";

export const AddTreatmentModal = ({
  open,
  onClose,
  account,
  refetch,
}: AddTreatmentProps) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [startDateValue, setStartDateValue] = useState<Date | null>(null);
  const [endDateValue, setEndDateValue] = useState<Date | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm<AddTreatmentFormInputs>({
    mode: "onChange",
    defaultValues: {
      program_type: "",
      start_date: null,
      end_date: null,
      specialist_id: null,
    },
  });

  const watchStartDate = watch("start_date");
  const watchEndDate = watch("end_date");
  const watchProgramType = watch("program_type");

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { options: specialistData, loading: loadingRecorders } =
    useSpecialistDropdown();

  const { data: getPrograms } = useQuery(GET_PROGRAMS, {
    fetchPolicy: "cache-and-network",
  });

  const ProgramTypes = getPrograms?.programEnums;

  const {
    data: TreatmentData,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loading: TreatmentLoading,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: AllProgramsRefetch,
  } = useQuery<TreatmentHistoryResponse>(GET_TREATMENT_HISTORY, {
    variables: { accountId: account?.id },
    fetchPolicy: "network-only",
    skip: !account?.id,
  });

  const [addTreatment, { error: treatmentError, loading }] =
    useMutation(ADD_TREATMENT);

  useEffect(() => {
    if (treatmentError) {
      setErrorMessage(treatmentError.message);
    }
  }, [treatmentError]);

  const handleChangeStartDate = (newValue: Date | null) => {
    const date = newValue ? new Date(newValue) : null;
    setStartDateValue(date);
    setValue("start_date", date);
  };

  const handleChangeEndDate = (newValue: Date | null) => {
    if (!newValue) {
      setEndDateValue(null);
      setValue("end_date", null);
      return;
    }

    const inputDate = new Date(newValue);
    if (isNaN(inputDate.getTime())) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Invalid date provided to handleChangeEndDate:", newValue);
      return;
    }

    // Set to end of day in UTC to ensure consistency across environments
    const endOfDayDate = new Date(inputDate);
    endOfDayDate.setUTCHours(23, 59, 59, 999);

    setEndDateValue(endOfDayDate);
    setValue("end_date", endOfDayDate);
  };

  const isSubmitDisabled = () => {
    if (loading) return true;

    if (watchProgramType === "MAINTENANCE") {
      return !watchStartDate || !watchEndDate;
    }

    return false;
  };

  const getEndDateValidationRules = () => {
    const baseRules = {
      validate: (value: Date | null) => {
        // If no end date is provided, validation passes for non-maintenance programs
        if (!value) {
          // Only validate empty end date for non-maintenance programs
          if (
            watchProgramType &&
            watchProgramType.toUpperCase() !== "MAINTENANCE"
          ) {
            return true;
          }
        }

        if (!watchStartDate || !value) return true;

        const startDate = new Date(watchStartDate);
        const endDate = new Date(value);

        // Check if end date is after start date
        if (endDate <= startDate) {
          return "End date must be after start date";
        }

        // Check if dates are the same (comparing date part only using UTC)
        const startDateOnly = new Date(startDate);
        startDateOnly.setUTCHours(0, 0, 0, 0);
        const endDateOnly = new Date(endDate);
        endDateOnly.setUTCHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === endDateOnly.getTime()) {
          return "End date cannot be the same as start date";
        }

        return true;
      },
    };

    if (watchProgramType && watchProgramType.toUpperCase() === "MAINTENANCE") {
      return {
        ...baseRules,
        required: "End date is required for maintenance program",
      };
    }

    return baseRules;
  };

  const onSubmit: SubmitHandler<AddTreatmentFormInputs> = async (response) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formatDate = (date: Date | null) =>
      date ? new Date(date).toISOString().split("T")[0] : null;

    const newStart = new Date(response.start_date!);
    const newEnd = response.end_date ? new Date(response.end_date) : null;

    // Check for date overlap with any existing programs (regardless of type)
    const overlappingProgram = TreatmentData?.treatmentHistory?.find(
      (treatment) => {
        const existingStart = new Date(treatment.start_date);
        const existingEnd = treatment.end_date
          ? new Date(treatment.end_date)
          : null;

        // Check for overlap using the same logic as backend
        // If either program has no end date, check if the start dates conflict
        if (!newEnd || !existingEnd) {
          // If new program has no end date and starts before or on existing program's start
          if (!newEnd && newStart <= existingStart) {
            // Check if existing program ends after new program starts (or has no end)
            return !existingEnd || existingEnd >= newStart;
          }
          // If existing program has no end date and starts before or on new program's start
          if (!existingEnd && existingStart <= newStart) {
            // Check if new program ends after existing program starts (or has no end)
            return !newEnd || newEnd >= existingStart;
          }
        }

        // Both have end dates - check for standard overlap
        // Programs overlap if one starts before the other ends
        return (
          newStart <= (existingEnd || existingStart) &&
          existingStart <= (newEnd || newStart)
        );
      }
    );

    if (overlappingProgram) {
      const existingStartStr = new Date(overlappingProgram.start_date)
        .toISOString()
        .split("T")[0];
      const existingEndStr = overlappingProgram.end_date
        ? new Date(overlappingProgram.end_date).toISOString().split("T")[0]
        : "ongoing";

      setErrorMessage(
        `Date range overlaps with existing ${overlappingProgram.program_type} program (Round ${overlappingProgram.round}: ${existingStartStr} to ${existingEndStr})`
      );
      return;
    }

    try {
      await addTreatment({
        variables: {
          program_type: response.program_type,
          start_date: response.start_date,
          end_date: response.end_date,
          account_id: account.id,
          specialist_id: response.specialist_id,
        },
      });

      // Close modal first
      onClose();
      reset();
      setStartDateValue(null);
      setEndDateValue(null);

      // Then show success message and refetch after a brief delay
      setTimeout(() => {
        setSuccessMessage("Treatment added successfully!");
        refetch();
      }, 100);
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to add treatment:", error);
      setErrorMessage("Failed to add treatment. Please try again.");
    }
  };

  const handleClose = () => {
    onClose();
    reset();
    setStartDateValue(null);
    setEndDateValue(null);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <UserModalContainer>
          <Box sx={ModalStyle}>
            <Grid container justifyContent="space-between">
              <Typography variant="h6">Add Treatment Record</Typography>
              <Button
                className="button"
                variant="outlined"
                onClick={handleClose}
              >
                X
              </Button>
            </Grid>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <label>Program Type*</label>
                    <Controller
                      name="program_type"
                      control={control}
                      rules={{ required: "Program type is required" }}
                      render={({ field }) => (
                        <MuiSelect {...field} displayEmpty>
                          <MenuItem value="" disabled>
                            Select a program type
                          </MenuItem>
                          {ProgramTypes?.map((program: string) => (
                            <MenuItem key={program} value={program}>
                              {program}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      )}
                    />
                    <small style={{ color: "#d32f2f" }}>
                      {errors.program_type?.message}
                    </small>
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <label>Start Date*</label>
                    <Controller
                      name="start_date"
                      control={control}
                      rules={{ required: "Start date is required" }}
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      render={({ field: { ref, ...rest } }) => (
                        <DateTimePickerComponent
                          dateValue={startDateValue}
                          handleChange={handleChangeStartDate}
                          label="Treatment Start Date"
                          required
                          {...rest}
                        />
                      )}
                    />
                    <small style={{ color: "#d32f2f" }}>
                      {errors.start_date?.message}
                    </small>
                  </Stack>
                </Grid>

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <label>
                      End Date{watchProgramType === "MAINTENANCE" && "*"}
                    </label>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={getEndDateValidationRules()}
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      render={({ field: { ref, ...rest } }) => (
                        <DateTimePickerComponent
                          dateValue={endDateValue}
                          handleChange={handleChangeEndDate}
                          label="Treatment End Date"
                          required={watchProgramType === "MAINTENANCE"}
                          {...rest}
                        />
                      )}
                    />
                    <small style={{ color: "#d32f2f" }}>
                      {errors.end_date?.message}
                    </small>
                  </Stack>
                </Grid>

                {/* <Grid item xs={6}>
                  <Stack spacing={1}>
                    <label>Round Number</label>
                    <Controller
                      name="roundNumber"
                      control={control}
                      rules={{
                        required: "Round number is required",
                        min: { value: 1, message: "Min value is 1" },
                        max: { value: 99, message: "Max value is 99" },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          inputProps={{ min: 1, max: 99 }}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10) || 1;
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <Typography color="error" variant="caption">
                      {errors?.roundNumber?.message}
                    </Typography>
                  </Stack>
                </Grid> */}

                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <label>Therapist*</label>
                    <Controller
                      name="specialist_id"
                      control={control}
                      rules={{ required: "Therapist is required" }}
                      render={({ field }) => (
                        <MuiSelect
                          {...field}
                          displayEmpty
                          value={field.value ?? ""}
                        >
                          <MenuItem value="" disabled>
                            Select a therapist
                          </MenuItem>
                          {specialistData?.map((spec) => (
                            <MenuItem
                              style={{ textTransform: "capitalize" }}
                              key={spec.firstName}
                              value={spec.value}
                            >
                              {spec.firstName}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      )}
                    />
                    <small style={{ color: "#d32f2f" }}>
                      {errors.specialist_id?.message}
                    </small>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Grid container justifyContent="flex-end" spacing={2}>
                    <Grid item>
                      <Button
                        className="button"
                        variant="contained"
                        onClick={handleClose}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className="button"
                        type="submit"
                        variant="contained"
                        disabled={isSubmitDisabled()}
                      >
                        {loading ? "Adding..." : "Add Treatment"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Box>
        </UserModalContainer>
      </Modal>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(undefined)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorMessage(undefined)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(undefined)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSuccessMessage(undefined)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
